<template>
  <dashboard-layout title="Natal Chart Reports" subtitle="Live generation queue, results, cost & errors">
    <template #actions>
      <span class="live-dot" :class="{ on: watching }" :title="watching ? 'Live' : 'Offline'"></span>
      <search-box v-model="search" placeholder="Search user / report id / name…" />
      <button class="refresh-btn" @click="reloadAll" :disabled="loading">{{ loading ? 'Loading…' : 'Refresh' }}</button>
    </template>

    <div v-if="error" class="error-banner">⚠ {{ error }}</div>

    <!-- 3. STATS -->
    <stat-grid>
      <stat-card label="Avg cost / report" :value="formatCost(avgReportCost)" icon="dollar" accent>
        <template #sub>{{ rs ? rs.count : 0 }} generations</template>
      </stat-card>
      <stat-card label="Total spent" :value="formatCost(rs ? rs.totalCostUSD : 0)" icon="creditcard">
        <template #sub>reports only</template>
      </stat-card>
      <stat-card label="Reports" :value="(rs ? rs.count : 0).toLocaleString('en-US')" icon="filetext">
        <template #sub>{{ rs ? rs.completed : 0 }} done · {{ rs ? rs.partial : 0 }} partial · {{ rs ? rs.failed : 0 }} failed</template>
      </stat-card>
      <stat-card label="Completion rate" :value="completionRate" icon="activity">
        <template #sub>{{ rs ? rs.repairs : 0 }} repairs</template>
      </stat-card>
      <stat-card label="Avg duration" :value="formatMin(avgReportMs)" icon="sliders">
        <template #sub v-if="rs && rs.lastAtMs">last {{ formatDate(rs.lastAtMs) }}</template>
      </stat-card>
      <stat-card label="Teaser avg cost" :value="formatCost(avgTeaserCost)" icon="dollar">
        <template #sub>{{ ts ? ts.count : 0 }} teasers · billed separately</template>
      </stat-card>
      <stat-card label="Teaser avg time" :value="formatMs(avgTeaserMs)" icon="activity">
        <template #sub>preview tab</template>
      </stat-card>
    </stat-grid>

    <!-- 1. LIVE QUEUE -->
    <data-panel>
      <template #title>Live queue <span class="muted">({{ queue.length }})</span></template>
      <template #actions>
        <span v-if="stalledCount" class="stalled-flag">⚠ {{ stalledCount }} stalled</span>
      </template>
      <div v-if="!queue.length" class="queue-empty">Queue is empty — nothing generating right now.</div>
      <div v-else class="table-scroll">
        <table class="nc-table">
          <thead>
            <tr><th>State</th><th>Report</th><th>User</th><th>Person</th><th>Lang</th><th>Queued</th><th>Elapsed</th><th>Progress</th><th>Waiting on</th></tr>
          </thead>
          <tbody>
            <tr v-for="r in queue" :key="r.id" :class="{ 'row-stalled': isStalled(r) }">
              <td><status-badge :variant="stateVariant(r.state)" :label="r.state" /><span v-if="isStalled(r)" class="stalled-tag">stalled</span></td>
              <td class="mono">{{ truncate(r.reportId, 14) }}</td>
              <td><a v-if="r.userId" :href="`/user/${r.userId}`" class="user-link" @click.prevent="openUser(r.userId, $event)">{{ truncate(r.userId, 10) }}</a><span v-else class="muted">—</span></td>
              <td>{{ r.person.name || '—' }} <span class="kind-tag">{{ r.person.kind || '?' }}</span></td>
              <td>{{ r.language || '—' }}</td>
              <td class="nowrap">{{ formatDate(r.queuedAtMs) }}</td>
              <td class="mono">{{ elapsed(r) }}</td>
              <td>
                <div class="prog"><div class="prog-fill" :style="{ width: (readyCount(r) / 15 * 100) + '%' }"></div></div>
                <span class="prog-txt">{{ readyCount(r) }}/15</span>
              </td>
              <td class="waiting">{{ notReadyParts(r).map(sliceLabel).join(', ') || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </data-panel>

    <!-- 2. RECENT REPORTS -->
    <data-panel flush>
      <template #title>Recent reports <span class="muted">({{ filteredRecent.length }})</span></template>
      <loading-state v-if="loading && !reports.length" label="Loading reports…" />
      <data-table
        v-else
        :columns="recentColumns"
        :rows="filteredRecent"
        row-key="id"
        clickable
        :expanded-key="expandedReport"
        @row-click="(row) => (expandedReport = expandedReport === row.id ? null : row.id)"
      >
        <template #cell-created="{ row }">
          <span class="date-cell"><span class="caret" :class="{ open: expandedReport === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
        </template>
        <template #cell-state="{ row }"><status-badge :variant="stateVariant(row.state)" :label="row.state || '—'" /></template>
        <template #cell-user="{ row }">
          <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
          <span v-else class="muted">—</span>
        </template>
        <template #cell-person="{ row }">{{ row.person.name || '—' }} <span class="kind-tag">{{ row.person.kind || '?' }}</span></template>
        <template #cell-title="{ row }"><span class="ellip" :title="row.title">{{ row.title || '—' }}</span></template>
        <template #cell-cost="{ row }"><span class="strong">{{ row.costUsd ? formatCost(row.costUsd) : '—' }}</span></template>
        <template #cell-duration="{ row }">{{ row.durationMs ? formatMin(row.durationMs) : '—' }}</template>
        <template #cell-failed="{ row }">
          <span v-if="row.failed.length" class="failed">{{ row.failed.length }}</span>
          <span v-else class="muted">—</span>
        </template>
        <template #cell-pdf="{ row }">
          <a v-if="row.pdf && row.pdf.url" :href="row.pdf.url" target="_blank" rel="noopener" class="pdf-link" @click.stop>{{ row.pdf.pages }}p · {{ formatKB(row.pdf.bytes) }}</a>
          <span v-else class="muted">—</span>
        </template>
        <template #empty>No natal chart reports yet — the first run is being tested.</template>

        <template #expanded="{ row }">
          <div class="rep-detail">
            <!-- parts -->
            <div class="rd-section">
              <div class="rd-title">Parts <span class="muted">({{ readyCount(row) }}/15 ready)</span></div>
              <div class="parts-grid">
                <span v-for="p in PARTS" :key="p" class="part-chip" :class="'ps-' + partState(row, p)">
                  {{ sliceLabel(p) }}<span v-if="row.partsReadyAtMs[p]" class="part-at">{{ formatClock(row.partsReadyAtMs[p]) }}</span>
                </span>
              </div>
            </div>
            <!-- person + bigthree + meta -->
            <div class="rd-cols">
              <div class="rd-section">
                <div class="rd-title">Person</div>
                <div class="kv"><span>Name</span><b>{{ row.person.name || '—' }}</b></div>
                <div class="kv"><span>Kind</span><b>{{ row.person.kind || '—' }}<template v-if="row.person.relationship"> · {{ row.person.relationship }}</template></b></div>
                <div class="kv"><span>Born</span><b>{{ row.person.birthDate || '—' }} {{ row.person.birthTime || '' }}</b></div>
                <div class="kv"><span>Place</span><b>{{ row.person.birthPlace || '—' }}</b></div>
              </div>
              <div class="rd-section">
                <div class="rd-title">Big three</div>
                <div class="kv"><span>Sun</span><b>{{ row.bigThree.sun || '—' }}</b></div>
                <div class="kv"><span>Moon</span><b>{{ row.bigThree.moon || '—' }}</b></div>
                <div class="kv"><span>Rising</span><b>{{ row.bigThree.rising || '—' }}</b></div>
              </div>
              <div class="rd-section">
                <div class="rd-title">Meta</div>
                <div class="kv"><span>Report</span><b class="mono">{{ row.reportId }}</b></div>
                <div class="kv"><span>Teaser</span><b>{{ row.teaserId ? 'yes' : 'no' }}</b></div>
                <div class="kv" v-if="row.purchase"><span>Product</span><b class="mono">{{ row.purchase.productId || '—' }}</b></div>
                <div class="kv" v-if="row.purchase"><span>Txn</span><b class="mono">{{ truncate(row.purchase.transactionId, 18) }}</b></div>
                <div class="kv" v-if="row.pdf"><span>PDF</span><b><a :href="row.pdf.url" target="_blank" rel="noopener" class="pdf-link">{{ row.pdf.pages }}p · {{ formatKB(row.pdf.bytes) }}</a></b></div>
              </div>
            </div>
            <!-- chapters -->
            <div v-if="row.chapters.length" class="rd-section">
              <div class="rd-title">Chapters <span class="muted">({{ row.chapters.length }})</span></div>
              <ol class="chapters">
                <li v-for="(c, i) in row.chapters" :key="i">{{ c.title }}</li>
              </ol>
            </div>
          </div>
        </template>
      </data-table>
    </data-panel>

    <!-- 4. GENERATIONS + charts -->
    <template v-if="generations.length">
      <div class="dual-grid">
        <data-panel title="Cost per day (USD)">
          <div class="chart-box"><canvas ref="costCanvas"></canvas></div>
        </data-panel>
        <data-panel title="Cost by part">
          <div class="chart-box"><canvas ref="partCanvas"></canvas></div>
        </data-panel>
      </div>
    </template>

    <data-panel flush>
      <template #title>Generations history <span class="muted">({{ generations.length }})</span></template>
      <data-table
        :columns="genColumns"
        :rows="generations"
        row-key="id"
        clickable
        :expanded-key="expandedGen"
        @row-click="(row) => (expandedGen = expandedGen === row.id ? null : row.id)"
      >
        <template #cell-created="{ row }">
          <span class="date-cell"><span class="caret" :class="{ open: expandedGen === row.id }">▸</span> {{ formatDate(row.createdAtMs) }}</span>
        </template>
        <template #cell-state="{ row }">
          <status-badge :variant="stateVariant(row.state)" :label="row.state || '—'" />
          <status-badge v-if="row.partialRepair" variant="info" label="repair" class="repair-tag" />
        </template>
        <template #cell-user="{ row }">
          <a v-if="row.userId" :href="`/user/${row.userId}`" class="user-link" @click.stop.prevent="openUser(row.userId, $event)">{{ truncate(row.userId, 10) }}</a>
          <span v-else class="muted">—</span>
        </template>
        <template #cell-cost="{ row }"><span class="strong">{{ formatCost(row.costUsd) }}</span></template>
        <template #cell-duration="{ row }">{{ formatMin(row.totalMs) }}</template>
        <template #cell-tokens="{ row }">{{ formatTokens(row.inputTokens) }} / {{ formatTokens(row.outputTokens) }}<span v-if="row.thinkingTokens" class="think"> +{{ formatTokens(row.thinkingTokens) }}t</span></template>
        <template #cell-teaser="{ row }">{{ row.hadTeaser ? '✓' : '—' }}</template>
        <template #cell-failed="{ row }"><span v-if="row.slicesFailed.length" class="failed">{{ row.slicesFailed.join(', ') }}</span><span v-else class="muted">—</span></template>
        <template #empty>No natal_chart generation rows yet.</template>

        <template #expanded="{ row }">
          <div class="slice-detail">
            <table class="slice-table">
              <thead><tr><th>Part</th><th>Model</th><th>OK</th><th>Cost</th><th>Duration</th><th>In/Out</th><th>Think</th><th>Att.</th></tr></thead>
              <tbody>
                <tr v-for="p in PARTS" :key="p" :class="{ 'slice-missing': !row.slices[p] }">
                  <td class="sl-name">{{ sliceLabel(p) }}</td>
                  <td class="sl-model">{{ modelShort(sliceOf(row, p).model) }}</td>
                  <td><span v-if="row.slices[p]" :class="sliceOf(row, p).ok ? 'ok-yes' : 'ok-no'">{{ sliceOf(row, p).ok ? '✓' : '✕' }}</span><span v-else class="muted">—</span></td>
                  <td>{{ row.slices[p] ? formatCost(num(sliceOf(row, p).costUsd)) : '—' }}</td>
                  <td>{{ row.slices[p] ? formatMs(num(sliceOf(row, p).ms)) : '—' }}</td>
                  <td>{{ row.slices[p] ? (formatTokens(num(sliceOf(row, p).inputTokens)) + ' / ' + formatTokens(num(sliceOf(row, p).outputTokens))) : '—' }}</td>
                  <td>{{ row.slices[p] && sliceOf(row, p).thinkingTokens ? formatTokens(num(sliceOf(row, p).thinkingTokens)) : '—' }}</td>
                  <td>{{ row.slices[p] ? num(sliceOf(row, p).attempts) : '—' }}</td>
                </tr>
              </tbody>
            </table>
            <div class="slice-foot">
              <span>Report: <code>{{ row.reportId || '—' }}</code></span>
              <span v-if="row.promptVersion">Prompt: <code>{{ row.promptVersion }}</code></span>
            </div>
          </div>
        </template>
      </data-table>
    </data-panel>

    <!-- 5. ERRORS -->
    <data-panel flush>
      <template #title>Errors <span class="muted">({{ errorLogs.length }})</span> <span class="muted small">· last 7 days</span></template>
      <data-table :columns="errorColumns" :rows="errorLogs" row-key="id">
        <template #cell-time="{ row }"><span class="nowrap">{{ formatDate(row.createdAtMs) }}</span></template>
        <template #cell-level="{ row }"><status-badge :variant="row.level === 'error' ? 'danger' : 'warning'" :label="row.level" /></template>
        <template #cell-functionName="{ row }"><span class="mono">{{ row.functionName }}</span></template>
        <template #cell-message="{ row }"><span class="ellip wide" :title="row.message">{{ row.message || '—' }}</span></template>
        <template #cell-error="{ row }"><span class="ellip" :title="row.error">{{ row.error || '—' }}</span></template>
        <template #cell-reportId="{ row }"><span class="mono">{{ truncate(row.reportId, 14) || '—' }}</span></template>
        <template #empty>No errors in the last 7 days for the natal-chart functions. 🎉</template>
      </data-table>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Chart } from 'chart.js/auto'
import { FirebaseNatalChartService, NATAL_PARTS } from '../services/firebaseNatalChartService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const QUEUE_STATES = ['queued', 'generating']
const STALL_QUEUED_MS = 5 * 60 * 1000
const STALL_GENERATING_MS = 35 * 60 * 1000
const PART_COLORS = ['#6c63ff', '#4ade80', '#f8bf71', '#f87171', '#22d3ee', '#c084fc', '#fb7185', '#a3e635', '#38bdf8', '#fbbf24', '#f472b6', '#2dd4bf', '#a78bfa', '#facc15', '#60a5fa']

const recentColumns = [
  { key: 'created', label: 'Created', width: '150px' },
  { key: 'state', label: 'State', width: '100px' },
  { key: 'user', label: 'User', width: '100px' },
  { key: 'person', label: 'Person', width: '150px' },
  { key: 'title', label: 'Title' },
  { key: 'cost', label: 'Cost', width: '80px' },
  { key: 'duration', label: 'Duration', width: '80px' },
  { key: 'failed', label: 'Fail', width: '50px' },
  { key: 'pdf', label: 'PDF', width: '110px' }
]
const genColumns = [
  { key: 'created', label: 'Created', width: '150px' },
  { key: 'state', label: 'State', width: '120px' },
  { key: 'user', label: 'User', width: '100px' },
  { key: 'cost', label: 'Cost', width: '80px' },
  { key: 'duration', label: 'Duration', width: '80px' },
  { key: 'tokens', label: 'Tok in/out', width: '140px' },
  { key: 'teaser', label: 'Teaser', width: '60px' },
  { key: 'failed', label: 'Failed parts' }
]
const errorColumns = [
  { key: 'time', label: 'Time', width: '150px' },
  { key: 'level', label: 'Level', width: '80px' },
  { key: 'functionName', label: 'Function', width: '200px' },
  { key: 'message', label: 'Message' },
  { key: 'error', label: 'Error', width: '220px' },
  { key: 'reportId', label: 'Report', width: '130px' }
]

function num(v) {
  if (typeof v === 'number') return v
  if (v == null) return 0
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

export default {
  name: 'NatalChartReportsDashboard',
  components: { DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, LoadingState, StatusBadge },
  setup() {
    const router = useRouter()
    const service = new FirebaseNatalChartService()

    const reports = ref([])
    const generations = ref([])
    const errorLogs = ref([])
    const rs = ref(null) // report stats
    const ts = ref(null) // teaser stats
    const loading = ref(true)
    const error = ref('')
    const search = ref('')
    const watching = ref(false)
    const now = ref(Date.now())
    const expandedReport = ref(null)
    const expandedGen = ref(null)

    const costCanvas = ref(null)
    const partCanvas = ref(null)
    let costChart = null
    let partChart = null
    let unsub = null
    let ticker = null

    const reloadAll = async () => {
      loading.value = true
      error.value = ''
      try {
        const [stats, gens, errs] = await Promise.all([service.getStats(), service.getGenerations(), service.getErrorLogs()])
        rs.value = stats.report
        ts.value = stats.teaser
        generations.value = gens
        errorLogs.value = errs
      } catch (e) {
        console.error(e)
        const code = e && e.code ? e.code : ''
        if (code === 'permission-denied') error.value = 'Firestore denied a read (rule issue). Tell the backend which collection.'
        else error.value = (e && e.message) || 'Failed to load natal chart data.'
      } finally {
        loading.value = false
      }
    }

    const startWatch = () => {
      unsub = service.watchReports(
        rows => { reports.value = rows; watching.value = true },
        err => { watching.value = false; error.value = (err && err.message) || 'Live queue read failed.' }
      )
    }

    // ---- stats computed ----
    const avgReportCost = computed(() => (rs.value && rs.value.count) ? rs.value.totalCostUSD / rs.value.count : 0)
    const avgReportMs = computed(() => (rs.value && rs.value.count) ? rs.value.totalMs / rs.value.count : 0)
    const completionRate = computed(() => (rs.value && rs.value.count) ? Math.round((rs.value.completed / rs.value.count) * 100) + '%' : '—')
    const avgTeaserCost = computed(() => (ts.value && ts.value.count) ? ts.value.totalCostUSD / ts.value.count : 0)
    const avgTeaserMs = computed(() => (ts.value && ts.value.count) ? ts.value.totalMs / ts.value.count : 0)

    // ---- queue + recent ----
    const queue = computed(() =>
      reports.value.filter(r => QUEUE_STATES.includes(r.state)).sort((a, b) => b.queuedAtMs - a.queuedAtMs)
    )
    const filteredRecent = computed(() => {
      const q = search.value.toLowerCase().trim()
      let list = reports.value.slice().sort((a, b) => b.createdAtMs - a.createdAtMs)
      if (q) list = list.filter(r => `${r.userId} ${r.reportId} ${r.person.name || ''} ${r.title}`.toLowerCase().includes(q))
      return list.slice(0, 50)
    })

    const readyCount = (r) => NATAL_PARTS.filter(k => r.parts[k] === 'ready').length
    const notReadyParts = (r) => NATAL_PARTS.filter(k => r.parts[k] && r.parts[k] !== 'ready')
    const partState = (r, p) => {
      const v = r.parts[p]
      if (v === 'ready') return 'ready'
      if (v === 'failed') return 'failed'
      if (v === 'generating') return 'generating'
      return 'pending'
    }
    const isStalled = (r) => {
      if (r.state === 'queued' && !r.startedAtMs) return (now.value - r.queuedAtMs) > STALL_QUEUED_MS && r.queuedAtMs > 0
      if (r.state === 'generating') return r.startedAtMs > 0 && (now.value - r.startedAtMs) > STALL_GENERATING_MS
      return false
    }
    const stalledCount = computed(() => queue.value.filter(isStalled).length)
    const elapsed = (r) => {
      const start = r.startedAtMs || r.queuedAtMs
      if (!start) return '—'
      const end = r.finishedAtMs || now.value
      return formatMs(Math.max(0, end - start))
    }

    // ---- charts (from generations) ----
    const daySeries = computed(() => {
      const map = {}
      generations.value.forEach(g => {
        if (!g.createdAtMs) return
        const key = new Date(g.createdAtMs).toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' })
        map[key] = (map[key] || 0) + g.costUsd
      })
      return Object.keys(map).sort().map(date => ({ date, cost: map[date] }))
    })
    const partAgg = computed(() => {
      const totals = {}
      NATAL_PARTS.forEach(k => { totals[k] = 0 })
      generations.value.forEach(g => NATAL_PARTS.forEach(k => { const s = g.slices && g.slices[k]; if (s) totals[k] += num(s.costUsd) }))
      return NATAL_PARTS.map(k => ({ key: k, total: totals[k] })).filter(r => r.total > 0)
    })

    const destroyCharts = () => { [costChart, partChart].forEach(c => c && c.destroy()); costChart = partChart = null }
    const legendDark = { labels: { color: '#cfcfe1', padding: 10, font: { size: 11 }, usePointStyle: true } }
    const tooltipDark = { backgroundColor: '#1a1b2e', titleColor: '#fff', bodyColor: '#a9a9b3', borderColor: '#2c2d42', borderWidth: 1, padding: 12 }

    const renderCharts = () => {
      destroyCharts()
      if (!generations.value.length) return
      if (costCanvas.value) {
        const s = daySeries.value
        costChart = new Chart(costCanvas.value.getContext('2d'), {
          type: 'bar',
          data: { labels: s.map(d => d.date), datasets: [{ label: 'Cost', data: s.map(d => +d.cost.toFixed(6)), backgroundColor: 'rgba(108,99,255,0.65)', borderColor: '#6c63ff', borderWidth: 1, borderRadius: 4 }] },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { ...tooltipDark, callbacks: { label: c => ' $' + (c.parsed.y || 0).toFixed(4) } } }, scales: { x: { grid: { display: false }, ticks: { color: '#8b8b8b', font: { size: 11 } } }, y: { beginAtZero: true, grid: { color: '#2a2a2a' }, ticks: { color: '#8b8b8b', font: { size: 11 }, callback: v => '$' + v } } } }
        })
      }
      if (partCanvas.value) {
        const agg = partAgg.value
        partChart = new Chart(partCanvas.value.getContext('2d'), {
          type: 'doughnut',
          data: { labels: agg.map(a => sliceLabel(a.key)), datasets: [{ data: agg.map(a => +a.total.toFixed(6)), backgroundColor: agg.map((a, i) => PART_COLORS[i % PART_COLORS.length]), borderColor: '#1a1b2e', borderWidth: 2 }] },
          options: { responsive: true, maintainAspectRatio: false, cutout: '58%', plugins: { legend: { position: 'right', ...legendDark }, tooltip: { callbacks: { label: c => ` ${c.label}: $${(c.parsed || 0).toFixed(4)}` } } } }
        })
      }
    }

    // ---- helpers ----
    const sliceOf = (row, key) => (row.slices && row.slices[key]) || {}
    const sliceLabel = (k) => {
      if (!k) return '—'
      if (k.startsWith('chapter_')) return 'Ch ' + k.slice(8)
      return k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    }
    const stateVariant = (s) => ({ complete: 'success', generating: 'info', queued: 'warning', partial: 'warning', failed: 'danger' }[s] || 'neutral')
    const modelShort = (m) => !m ? '—' : m.replace('claude-', '').replace(/-\d{8}$/, '')
    const truncate = (t, n) => !t ? '' : (t.length > n ? t.slice(0, n) + '…' : t)
    const formatCost = n => '$' + (n || 0).toFixed(n && n < 0.01 ? 6 : 4)
    const formatTokens = n => { const v = n || 0; if (v < 1000) return String(v); if (v < 1e6) return (v / 1e3).toFixed(1) + 'K'; return (v / 1e6).toFixed(2) + 'M' }
    const formatMs = ms => { const v = Math.round(ms || 0); if (v < 1000) return v + 'ms'; if (v < 60000) return (v / 1000).toFixed(1) + 's'; const m = Math.floor(v / 60000); const s = Math.round((v % 60000) / 1000); return s ? `${m}m ${s}s` : `${m}m` }
    const formatMin = ms => { const v = ms || 0; if (!v) return '—'; if (v < 60000) return (v / 1000).toFixed(0) + 's'; return (v / 60000).toFixed(1) + 'm' }
    const formatKB = b => { const v = b || 0; if (v < 1024) return v + 'B'; if (v < 1048576) return (v / 1024).toFixed(0) + 'KB'; return (v / 1048576).toFixed(1) + 'MB' }
    const formatDate = ms => { if (!ms) return '—'; return new Date(ms).toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) }
    const formatClock = ms => { if (!ms) return ''; return new Date(ms).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) }
    const openUser = (uid, event) => { if (event.metaKey || event.ctrlKey) window.open(`/user/${uid}`, '_blank'); else router.push(`/user/${uid}`) }

    watch(generations, () => renderCharts(), { flush: 'post' })

    onMounted(() => {
      startWatch()
      reloadAll()
      ticker = setInterval(() => { now.value = Date.now() }, 1000)
    })
    onBeforeUnmount(() => {
      if (unsub) unsub()
      if (ticker) clearInterval(ticker)
      destroyCharts()
    })

    return {
      reports, generations, errorLogs, rs, ts, loading, error, search, watching, now,
      expandedReport, expandedGen, recentColumns, genColumns, errorColumns, PARTS: NATAL_PARTS,
      costCanvas, partCanvas,
      avgReportCost, avgReportMs, completionRate, avgTeaserCost, avgTeaserMs,
      queue, filteredRecent, stalledCount,
      reloadAll, readyCount, notReadyParts, partState, isStalled, elapsed, sliceOf, num,
      sliceLabel, stateVariant, modelShort, truncate, formatCost, formatTokens, formatMs, formatMin, formatKB, formatDate, formatClock, openUser
    }
  }
}
</script>

<style scoped>
.error-banner { background: rgba(248, 191, 113, 0.12); border: 1px solid rgba(248, 191, 113, 0.35); color: var(--warning); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.refresh-btn { padding: 9px 14px; background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: var(--radius); color: var(--text-primary); font-size: 13px; cursor: pointer; }
.refresh-btn:hover { background: var(--hover-bg); }
.refresh-btn:disabled { opacity: 0.5; cursor: default; }
.live-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--text-secondary); opacity: 0.5; }
.live-dot.on { background: var(--success); opacity: 1; box-shadow: 0 0 0 3px rgba(74, 222, 128, 0.2); animation: pulse 2s infinite; }
@keyframes pulse { 50% { opacity: 0.55; } }

.muted { color: var(--text-secondary); font-weight: 400; }
.small { font-size: 11px; }
.strong { font-weight: 600; }
.mono { font-family: monospace; font-size: 12px; }
.nowrap { white-space: nowrap; }
.ellip { display: inline-block; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }
.ellip.wide { max-width: 360px; }
.user-link { color: var(--primary-color); text-decoration: none; }
.user-link:hover { text-decoration: underline; }
.kind-tag { font-size: 10px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-secondary); background: var(--field-bg); border: 1px solid var(--panel-border); border-radius: 8px; padding: 1px 6px; margin-left: 4px; }
.failed { color: var(--danger); font-weight: 600; }
.caret { display: inline-block; width: 11px; color: var(--text-secondary); transition: transform 0.18s ease; }
.caret.open { transform: rotate(90deg); color: var(--primary-color); }
.repair-tag { margin-left: 6px; }
.think { color: var(--text-secondary); font-size: 11px; }
.pdf-link { color: var(--primary-color); text-decoration: none; font-size: 12px; }
.pdf-link:hover { text-decoration: underline; }

/* Live queue */
.stalled-flag { font-size: 12px; color: var(--danger); font-weight: 600; }
.queue-empty { padding: 20px 4px; color: var(--text-secondary); font-size: 13px; }
.table-scroll { overflow-x: auto; }
.nc-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.nc-table th, .nc-table td { padding: 9px 12px; text-align: left; border-bottom: 1px solid var(--border-soft); font-size: 12.5px; white-space: nowrap; }
.nc-table th { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); font-weight: 600; }
.nc-table td { color: var(--text-primary); }
.row-stalled { background: rgba(255, 107, 107, 0.08); }
.row-stalled td:first-child { box-shadow: inset 3px 0 0 var(--danger); }
.stalled-tag { margin-left: 6px; font-size: 9px; text-transform: uppercase; color: var(--danger); font-weight: 700; }
.prog { display: inline-block; width: 70px; height: 7px; background: rgba(255, 255, 255, 0.08); border-radius: 4px; overflow: hidden; vertical-align: middle; }
.prog-fill { height: 100%; background: linear-gradient(90deg, var(--primary-color), var(--gradient-2)); }
.prog-txt { font-size: 11px; color: var(--text-secondary); margin-left: 6px; }
.waiting { white-space: normal; max-width: 260px; font-size: 11.5px; color: var(--text-secondary); }

/* Report detail */
.rep-detail { padding: 14px 16px 18px; }
.rd-section { margin-bottom: 14px; }
.rd-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); margin-bottom: 8px; }
.rd-cols { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
.parts-grid { display: flex; flex-wrap: wrap; gap: 6px; }
.part-chip { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; padding: 3px 9px; border-radius: 12px; border: 1px solid var(--panel-border); background: var(--field-bg); color: var(--text-secondary); }
.part-chip.ps-ready { background: rgba(74, 222, 128, 0.14); color: var(--success); border-color: rgba(74, 222, 128, 0.32); }
.part-chip.ps-failed { background: rgba(255, 107, 107, 0.16); color: var(--danger); border-color: rgba(255, 107, 107, 0.38); }
.part-chip.ps-generating { background: rgba(34, 211, 238, 0.14); color: var(--info); border-color: rgba(34, 211, 238, 0.32); }
.part-at { font-size: 9.5px; opacity: 0.7; }
.kv { display: flex; gap: 8px; font-size: 12.5px; margin-bottom: 4px; }
.kv span { width: 70px; flex-shrink: 0; color: var(--text-secondary); }
.kv b { color: var(--text-primary); font-weight: 500; }
.chapters { margin: 0; padding-left: 18px; }
.chapters li { font-size: 12.5px; color: var(--text-primary); margin-bottom: 3px; }

/* charts */
.chart-box { height: 280px; position: relative; }
.dual-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); gap: 16px; }

/* slice detail (generations) */
.slice-detail { padding: 14px 16px 16px; }
.slice-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.slice-table th, .slice-table td { text-align: left; padding: 6px 10px; border-bottom: 1px solid var(--border-soft); font-size: 12px; }
.slice-table th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-secondary); font-weight: 600; }
.slice-table td { color: var(--text-primary); }
.slice-table tr:last-child td { border-bottom: none; }
.sl-name { font-weight: 600; }
.sl-model { color: var(--text-secondary); font-family: monospace; font-size: 11px; }
.slice-missing td { opacity: 0.45; }
.ok-yes { color: var(--success); font-weight: 700; }
.ok-no { color: var(--danger); font-weight: 700; }
.slice-foot { display: flex; gap: 18px; flex-wrap: wrap; margin-top: 10px; font-size: 12px; color: var(--text-secondary); }
.slice-foot code { background: var(--field-bg); padding: 1px 6px; border-radius: 4px; font-size: 11px; }
</style>
